import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { api } from "@/lib/api";
import { ROUTE_SEO } from "@/config/seoData";

const reveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const ServicesOverview = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/services/categories")
      .then((r) => setCategories(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo {...ROUTE_SEO.services} />
      <Section testid="services-overview-page" className="pt-32">
        <Container className="space-y-16">
          <SectionHeading
            as="h1"
            eyebrow="Services"
            title="Technology first. Growth always."
            description="Five disciplines covering the full journey from idea to shipped product to growing market presence. Every service is delivered by one accountable team."
            testid="services-overview-heading"
          />
          {loading && <p className="text-sm text-brand-mist" data-testid="services-loading">Loading services…</p>}
          {error && (
            <p className="text-sm text-red-400" data-testid="services-error">
              Services could not be loaded right now. Please try again shortly.
            </p>
          )}
          <div className="space-y-14" data-testid="services-category-list">
            {categories.map((cat, ci) => (
              <CategoryBlock key={cat.slug} category={cat} index={ci} />
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
};

const CategoryBlock = ({ category, index }) => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api
      .get(`/services/categories/${category.slug}`)
      .then((r) => setServices(r.data.services || []))
      .catch(() => {});
  }, [category.slug]);

  return (
    <motion.div
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      custom={index}
      className="space-y-6 border-t border-brand-mist/10 pt-10"
      data-testid={`category-block-${category.slug}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl space-y-3">
          <p className="eyebrow">0{index + 1} · {category.services} services</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-brand-snow sm:text-3xl">
            {category.name}
          </h2>
          <p className="text-sm leading-relaxed text-brand-mist sm:text-base">{category.intro}</p>
        </div>
        <Link
          to={`/services/${category.slug}`}
          data-testid={`category-link-${category.slug}`}
          className="group inline-flex shrink-0 items-center gap-2 text-sm font-medium text-brand-emerald"
        >
          Explore category
          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => (
          <li key={s.slug}>
            <Link
              to={`/services/${category.slug}/${s.slug}`}
              data-testid={`service-link-${s.slug}`}
              className="group flex h-full items-center justify-between gap-3 rounded-lg border border-brand-mist/12 bg-brand-charcoal px-5 py-4 transition-colors duration-200 hover:border-brand-emerald/35"
            >
              <span className="text-sm font-medium text-brand-snow">{s.name}</span>
              <ArrowUpRight
                size={15}
                className="shrink-0 text-brand-mist transition-colors duration-200 group-hover:text-brand-emerald"
              />
            </Link>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export const ServiceCategoryPage = () => {
  const { category: slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api
      .get(`/services/categories/${slug}`)
      .then((r) => {
        setData(r.data);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [slug]);

  return (
    <>
      <Seo
        title={data?.seo?.title?.replace(" | A2Z Rankings", "") || "Services"}
        description={data?.seo?.description}
        canonicalPath={data ? `/services/${data.slug}` : undefined}
        ogImage={data ? `${ROUTE_SEO.services.ogImage.replace("/og/page/services.png", `/og/category/${data.slug}.png`)}` : undefined}
        jsonLd={
          data
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.a2zrankings.com" },
                    { "@type": "ListItem", position: 2, name: "Services", item: "https://www.a2zrankings.com/services" },
                    { "@type": "ListItem", position: 3, name: data.name, item: `https://www.a2zrankings.com/services/${data.slug}` },
                  ],
                },
              ]
            : []
        }
      />
      <Section testid="service-category-page" className="pt-32">
        <Container className="space-y-12">
          {state === "loading" && <p className="text-sm text-brand-mist" data-testid="category-loading">Loading…</p>}
          {state === "error" && (
            <div className="space-y-4" data-testid="category-not-found">
              <h1 className="font-display text-3xl font-bold text-brand-snow">Category not found</h1>
              <SiteButton to="/services" variant="outline" testid="category-back-link">
                Back to all services
              </SiteButton>
            </div>
          )}
          {state === "ok" && data && (
            <>
              <div className="max-w-3xl space-y-5">
                <Link to="/services" data-testid="breadcrumb-services" className="eyebrow inline-flex items-center gap-2 hover:text-brand-snow">
                  Services
                </Link>
                <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
                  {data.name}
                </h1>
                <p className="text-base leading-relaxed text-brand-mist">{data.intro}</p>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="category-services-grid">
                {(data.services || []).map((s, i) => (
                  <motion.div key={s.slug} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                    <Link
                      to={`/services/${slug}/${s.slug}`}
                      data-testid={`category-service-card-${s.slug}`}
                      className="group flex h-full flex-col justify-between gap-6 rounded-xl border border-brand-mist/12 bg-brand-charcoal p-6 transition-colors duration-200 hover:border-brand-emerald/35"
                    >
                      <div className="space-y-3">
                        <h2 className="font-display text-lg font-semibold text-brand-snow">{s.name}</h2>
                        <p className="text-sm leading-relaxed text-brand-mist">{s.intro}</p>
                      </div>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-emerald">
                        Learn more
                        <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="rounded-2xl border border-brand-emerald/20 bg-brand-charcoal p-8 sm:p-10" data-testid="category-cta">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-xl space-y-3">
                    <h2 className="font-display text-xl font-bold text-brand-snow sm:text-2xl">
                      Not sure which service fits?
                    </h2>
                    <p className="text-sm text-brand-mist sm:text-base">
                      A discovery call is the fastest way to find out — tell us the outcome you need and we'll point
                      you straight.
                    </p>
                  </div>
                  <SiteButton to="/contact" testid="category-cta-discovery-call">
                    Book a free discovery call
                  </SiteButton>
                </div>
              </div>
            </>
          )}
        </Container>
      </Section>
    </>
  );
};
